/**
 * Demo Mode (Ghost) — Spec Bölüm 7.1.0
 *
 * 5 soruluk hızlı deneme:
 *   POST /api/public/demo/start    → sessionId + 5 soru
 *   POST /api/public/demo/answer   → her tıklamada
 *   POST /api/public/demo/complete → skor + sabit template rapor
 *
 * KRİTİK: AI çağrısı YOK — sabit template rapor (cost kontrol).
 * IP başına günlük 1 demo (Redis ile backend enforce ediyor).
 */
import { useState } from 'react';
import { Link } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Button, Card } from '../components/ui.js';
import { ReportMarkdown } from '../components/ReportMarkdown.js';
import { api, ApiError } from '../lib/api.js';

interface DemoOption {
  key: string;
  text: string;
}

interface DemoQuestion {
  id: string;
  topic: string;
  questionText: string;
  imageUrl: string | null;
  options: DemoOption[];
  estimatedTimeSec: number;
}

interface DemoStartResponse {
  sessionId: string;
  questions: DemoQuestion[];
  durationMin: number;
  expiresInSec: number;
}

interface DemoCompleteResponse {
  score: number;
  scoreLevel: string;
  correctCount: number;
  totalCount: number;
  reportMarkdown: string;
}

const TOPIC_LABELS: Record<string, string> = {
  PM: 'Parça Modelleme',
  AS: 'Assembly',
  DR: 'Drawing',
  SK: 'Sketch & Tasarım Niyeti',
  SM: 'Sac Metal',
  WD: 'Weldments',
  SF: 'Surfacing',
  CF: 'Configurations',
  GN: 'Genel SW',
  DT: 'Tasarım Teorisi',
};

const LEVEL_LABELS: Record<string, string> = {
  L0: 'Tanışma',
  L1_MINUS: 'Başlangıç',
  L1: 'CSWA Seviyesi',
  L2: 'CSWA Üzeri',
  L3: 'CSWP Seviyesi',
  L4: 'İleri Seviye',
};

type DemoState =
  | { phase: 'intro' }
  | { phase: 'loading' }
  | {
      phase: 'taking';
      session: DemoStartResponse;
      answers: Record<string, string>;
      currentIdx: number;
    }
  | { phase: 'submitting'; session: DemoStartResponse }
  | { phase: 'done'; result: DemoCompleteResponse };

export function DemoPage(): JSX.Element {
  const [state, setState] = useState<DemoState>({ phase: 'intro' });
  const [error, setError] = useState<string | null>(null);

  async function startDemo(): Promise<void> {
    setError(null);
    setState({ phase: 'loading' });
    try {
      const data = await api.post<DemoStartResponse>('/api/public/demo/start');
      setState({
        phase: 'taking',
        session: data,
        answers: {},
        currentIdx: 0,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'RATE_LIMIT') {
          setError(
            'Bugünlük demo hakkınızı kullandınız. Yarın tekrar deneyin veya ücretsiz hesap açın.',
          );
        } else {
          setError(err.message);
        }
      } else {
        setError('Demo başlatılamadı');
      }
      setState({ phase: 'intro' });
    }
  }

  async function submitAnswer(questionId: string, key: string): Promise<void> {
    if (state.phase !== 'taking') return;
    const newAnswers = { ...state.answers, [questionId]: key };
    setState({ ...state, answers: newAnswers });
    // Fire-and-forget — kullanıcıyı bekletme
    api
      .post('/api/public/demo/answer', {
        demoSessionId: state.session.sessionId,
        questionId,
        selectedOption: key,
      })
      .catch(() => {
        /* sessizce yut — son complete çağrısında sunucu kendi state'inden okuyor */
      });
  }

  function nextQuestion(): void {
    if (state.phase !== 'taking') return;
    if (state.currentIdx < state.session.questions.length - 1) {
      setState({ ...state, currentIdx: state.currentIdx + 1 });
    }
  }

  function prevQuestion(): void {
    if (state.phase !== 'taking') return;
    if (state.currentIdx > 0) {
      setState({ ...state, currentIdx: state.currentIdx - 1 });
    }
  }

  async function completeDemo(): Promise<void> {
    if (state.phase !== 'taking') return;
    setState({ phase: 'submitting', session: state.session });
    try {
      const result = await api.post<DemoCompleteResponse>(
        '/api/public/demo/complete',
        { demoSessionId: state.session.sessionId },
      );
      setState({ phase: 'done', result });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Demo tamamlanamadı';
      setError(msg);
      setState(state); // Geri al, kullanıcı tekrar dener
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-3xl px-6 py-12">
        {error && (
          <div className="mb-6">
            <Alert variant="warning">{error}</Alert>
          </div>
        )}

        {state.phase === 'intro' && (
          <DemoIntro onStart={startDemo} />
        )}

        {state.phase === 'loading' && (
          <Card>
            <p className="text-center text-slate-600">Demo hazırlanıyor…</p>
          </Card>
        )}

        {state.phase === 'taking' && (
          <DemoTaking
            session={state.session}
            answers={state.answers}
            currentIdx={state.currentIdx}
            onAnswer={submitAnswer}
            onNext={nextQuestion}
            onPrev={prevQuestion}
            onComplete={completeDemo}
          />
        )}

        {state.phase === 'submitting' && (
          <Card>
            <p className="text-center text-slate-600">Skorunuz hesaplanıyor…</p>
          </Card>
        )}

        {state.phase === 'done' && <DemoResult result={state.result} />}
      </main>
    </div>
  );
}

function DemoIntro({ onStart }: { onStart: () => void }): JSX.Element {
  return (
    <Card>
      <h1 className="text-3xl font-bold text-slate-900">
        Hızlı Deneme · 5 Soru
      </h1>
      <p className="mt-2 text-slate-600">
        BMU SkillProbe'un nasıl çalıştığını 5 dakikada gör. Sistemin gerçek
        sorularından, ücretsiz ve kayıtsız bir hızlı test.
      </p>

      <ul className="mt-6 space-y-2 text-sm text-slate-700">
        <li>✓ 5 farklı konudan birer soru</li>
        <li>✓ Yaklaşık 5 dakika</li>
        <li>✓ Kayıt gerektirmez</li>
        <li>✓ Anonim — KVKK güvende</li>
      </ul>

      <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <strong>Demo tek seferlik:</strong> Bugün bir kez deneyebilirsiniz.
        Ekibinize tam değerlendirme yaptırmak için{' '}
        <Link href="/yonetici/kayit" className="font-medium underline">
          ücretsiz hesap açın
        </Link>
        .
      </div>

      <Button onClick={onStart} className="mt-6 w-full">
        Demoya Başla
      </Button>
    </Card>
  );
}

function DemoTaking({
  session,
  answers,
  currentIdx,
  onAnswer,
  onNext,
  onPrev,
  onComplete,
}: {
  session: DemoStartResponse;
  answers: Record<string, string>;
  currentIdx: number;
  onAnswer: (questionId: string, key: string) => void;
  onNext: () => void;
  onPrev: () => void;
  onComplete: () => void;
}): JSX.Element {
  const q = session.questions[currentIdx];
  if (!q) return <Card>Soru bulunamadı</Card>;
  const selectedKey = answers[q.id];
  const isLast = currentIdx === session.questions.length - 1;
  const answeredCount = Object.keys(answers).length;
  const totalCount = session.questions.length;

  return (
    <>
      {/* Progress bar */}
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">
          Soru {currentIdx + 1} / {totalCount}
        </span>
        <span className="text-sm text-slate-500">
          {answeredCount} yanıtlandı
        </span>
      </div>
      <div className="mb-6 h-2 rounded-full bg-slate-200">
        <div
          className="h-full rounded-full bg-blue-900 transition-all"
          style={{ width: `${((currentIdx + 1) / totalCount) * 100}%` }}
        />
      </div>

      <Card>
        <div className="mb-3 inline-block rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-900">
          {TOPIC_LABELS[q.topic] ?? q.topic}
        </div>

        <h2 className="text-lg font-semibold text-slate-900">{q.questionText}</h2>

        {q.imageUrl && (
          <img
            src={q.imageUrl}
            alt="Soru görseli"
            className="mt-4 max-h-80 rounded-lg border border-slate-200"
          />
        )}

        <div className="mt-6 space-y-2">
          {q.options.map((opt) => {
            const isSelected = opt.key === selectedKey;
            return (
              <button
                type="button"
                key={opt.key}
                onClick={() => onAnswer(q.id, opt.key)}
                className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
                  isSelected
                    ? 'border-blue-900 bg-blue-50'
                    : 'border-slate-200 bg-white hover:border-blue-400 hover:bg-slate-50'
                }`}
              >
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                    isSelected
                      ? 'bg-blue-900 text-white'
                      : 'border border-slate-300 text-slate-600'
                  }`}
                >
                  {opt.key}
                </span>
                <span className="text-slate-800">{opt.text}</span>
              </button>
            );
          })}
        </div>
      </Card>

      <div className="mt-6 flex items-center justify-between gap-3">
        <Button variant="ghost" onClick={onPrev} disabled={currentIdx === 0}>
          ← Önceki
        </Button>

        {!isLast ? (
          <Button onClick={onNext} disabled={!selectedKey}>
            Sonraki →
          </Button>
        ) : (
          <Button
            onClick={onComplete}
            disabled={answeredCount < totalCount}
          >
            Demoyu Tamamla ({answeredCount}/{totalCount})
          </Button>
        )}
      </div>
    </>
  );
}

function DemoResult({ result }: { result: DemoCompleteResponse }): JSX.Element {
  const levelLabel = LEVEL_LABELS[result.scoreLevel] ?? result.scoreLevel;

  return (
    <>
      <Card>
        <div className="text-center">
          <p className="text-sm font-medium uppercase tracking-wider text-slate-500">
            Demo Skorunuz
          </p>
          <p className="mt-2 text-6xl font-bold text-blue-900">
            {result.score}
            <span className="text-2xl text-slate-400">/100</span>
          </p>
          <p className="mt-2 text-lg font-semibold text-slate-900">
            {levelLabel}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            {result.correctCount} / {result.totalCount} doğru cevap
          </p>
        </div>
      </Card>

      <Card className="mt-6">
        <ReportMarkdown text={result.reportMarkdown} />
      </Card>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <Link
          href="/yonetici/kayit"
          className="flex items-center justify-center rounded-lg bg-blue-900 px-6 py-3 text-base font-medium text-white hover:bg-blue-800"
        >
          Ücretsiz Tam Değerlendirme Başlat
        </Link>
        <Link
          href="/"
          className="flex items-center justify-center rounded-lg border-2 border-blue-900 px-6 py-3 text-base font-medium text-blue-900 hover:bg-blue-50"
        >
          Ana Sayfa
        </Link>
      </div>
    </>
  );
}

